import { cbField } from ".";
import { logEvent } from "../analytics";
import { CELL_STATE, cellToCoords } from "../cell";
import { XLINKS, XMLNS } from "../constants";
import { boardEl, infoEl, showTipBtnEl } from "../elements";
import { positionObject } from "../levels";
import "../ripple";
import store from "../store";
import { GAME_MODE, gameState, levelDef } from "./gameState";

const helperCell = document.getElementById("helper-cell");

function placeHelper(cb, cbField) {
  for (let i = 2; i < levelDef.fieldSize - 2; i++) {
    for (let j = 2; j < levelDef.fieldSize - 2; j++) {
      const cell = gameState.cells[i][j];
      if (cb(i, j) && cbField(i, j) && cell.state == CELL_STATE.FREE) {
        cell.id.setAttributeNS(XLINKS, "xlink:href", "#helper-cell");
        cell.id.classList.add("helper-cell");
        levelDef.helperCoords.push({ i: i - 2, j: j - 2 });
      }
    }
  }
}

export const toggleTip = (btnClick = true) => {
  gameState.showTip = !gameState.showTip;
  const remove = removeHelperCellsIfExist(btnClick);
  if (remove && !btnClick) return;
  if (!remove && btnClick) logEvent("show_tip");
  levelDef.helperCoords = [];
  if (remove) helperCell.firstElementChild.style.animation = null;
  else {
    helperCell.firstElementChild.style.animation =
      "helper ease-in-out 1.2s infinite";

    for (const [shape, shapeVariable] of levelDef.helper.placement.shapes) {
      const place = (cb) => placeHelper(cb, cbField);
      positionObject(place, levelDef.fieldSize, shape, shapeVariable);
    }
  }
  if (levelDef.helper.showPointer) createHelperPointers(remove);
};

const removeHelperCellsIfExist = (remove) => {
  const helperPointers = Array.from(
    boardEl.getElementsByClassName("helper-cell")
  );
  if (helperPointers.length) {
    if (remove)
      helperPointers.forEach((el) => {
        el.setAttributeNS(XLINKS, "xlink:href", "#free-cell");
        el.classList.remove("helper-cell");
      });
    return true;
  }
  return false;
};

const createHelperPointers = (remove) => {
  if (remove) {
    removeHelperPointer();
    return;
  }
  if (levelDef.helperCoords.length === 0) return;

  const pathData = buildPathData(levelDef.helperCoords);

  if (levelDef.helper.loop) {
    appendAnimateMotionLoop(pathData);
  } else {
    appendAnimateMotionSingleCycle(pathData);
  }
};

function removeHelperPointer() {
  const helperPointerEl = document.getElementById("helper-pointer");
  if (helperPointerEl) helperPointerEl.parentNode.removeChild(helperPointerEl);
  Array.from(boardEl.getElementsByClassName("helper-cell")).forEach((el) => {
    el.setAttributeNS(XLINKS, "xlink:href", "#free-cell");
    el.classList.remove("helper-cell");
  });
}

function createUseElement() {
  const use = document.createElementNS(XMLNS, "use");
  use.setAttributeNS(XLINKS, "xlink:href", "#pointer");
  use.setAttributeNS(null, "id", "helper-pointer");
  return use;
}

function buildPathData(coords) {
  return coords.map(({ i, j }, index) => {
    const { x, y } = cellToCoords(i, j);
    return `${x - 15} ${y + 15}${index < coords.length - 1 ? " L" : ""}`;
  });
}

function appendAnimateMotionLoop(pathData) {
  const use = createUseElement();
  const animateMotion = createAnimateMotionElement(
    "indefinite",
    levelDef.helperCoords.length * 1000,
    pathData.concat(pathData.slice(0, -1).reverse(), "Z")
  );
  use.appendChild(animateMotion);
  boardEl.appendChild(use);
}

function appendAnimateMotionSingleCycle(pathData) {
  const addAnimateMotion = () => {
    const oldUse = document.getElementById("helper-pointer");
    if (oldUse || !levelDef.helperCoords.length || !gameState.showTip) return;
    const use = createUseElement();
    const duration = (levelDef.helperCoords.length / 2) * 1000;
    const fadeInDuration = 100; // Duration of the fade-in effect in milliseconds
    const fadeOutDuration = 500; // Duration of the fade-out effect in milliseconds
    const totalDuration = duration + fadeOutDuration;
    const animateMotion = createAnimateMotionElement(
      "1",
      duration,
      pathData,
      true
    );
    use.appendChild(animateMotion);
    boardEl.appendChild(use);
    use.classList.add("fade-in");
    animateMotion.beginElementAt(0);
    setTimeout(fadeOut, duration - fadeInDuration, use);
    setTimeout(
      cleanAndaddAnimateMotion,
      totalDuration,
      use,
      animateMotion,
      addAnimateMotion
    );
  };

  addAnimateMotion();
}

function cleanAndaddAnimateMotion(use, animateMotion, addAnimateMotion) {
  if (use.parentNode) use.parentNode.removeChild(use);
  else use.removeChild(animateMotion);
  addAnimateMotion();
}

function fadeOut(use) {
  use.classList.remove("fade-in");
  use.classList.add("fade-out");
}

function createAnimateMotionElement(
  repeatCount,
  duration,
  pathData,
  freeze = false
) {
  const animateMotion = document.createElementNS(XMLNS, "animateMotion");
  animateMotion.setAttributeNS(null, "repeatCount", repeatCount);
  animateMotion.setAttributeNS(null, "dur", `${duration}ms`);
  if (freeze) animateMotion.setAttributeNS(null, "fill", "freeze");
  animateMotion.setAttributeNS(null, "path", `M ${pathData.join(" ")}`);
  return animateMotion;
}

let inactivityTimer;

export function hideTipIfNecessary() {
  if (
    store.levelIdx == 0 ||
    (levelDef.helper.maxSteps != null && store.step > levelDef.helper.maxSteps)
  )
    showTipBtnEl.style.display = "none";
}

function toggleTipIfNecessary() {
  if (
    infoEl.style.visibility === "hidden" &&
    store.screenName === "game" &&
    levelDef.helper &&
    (levelDef.helper.maxSteps == null || store.step < levelDef.helper.maxSteps)
  )
    toggleTip(false);
}

export const stopToggleTipTimer = () => clearTimeout(inactivityTimer);

export function resetToggleTipTimer() {
  stopToggleTipTimer();
  if (
    gameState.mode != GAME_MODE.STORY ||
    infoEl.style.visibility === "visible" ||
    (levelDef.helper.maxSteps != null && store.step > levelDef.helper.maxSteps)
  )
    return;
  if (levelDef.helper.placement)
    inactivityTimer = setTimeout(
      toggleTipIfNecessary,
      (store.levelIdx == 0 ? 1.5 : 20) * 1000
    );
}
